import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { LucideIcon } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { formatClock } from '../utils/schedulePrefs';

interface ChipProps {
  label: string;
  color?: string;
  size?: 'xs' | 'sm';
  dot?: boolean;
  className?: string;
  /** Full-strength background + white text, instead of the usual translucent
   *  tinted-background/coloured-text look. For a chip that needs to read as
   *  a real button (e.g. Home's "View itinerary" link) rather than a status
   *  badge. */
  solid?: boolean;
  /** Hex alpha suffix (e.g. "1a" = 10%, "40" = 25%) for the translucent
   *  background when `solid` is false. Defaults to the usual light tint;
   *  raise it for a chip sitting on a busy background (e.g. the floating
   *  blobs) that needs to read as more opaque. */
  bgAlpha?: string;
}

export function Chip({ label, color = "#f59e0b", size = "sm", dot, className = "", solid = false, bgAlpha = "1a" }: ChipProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-bold tracking-wider whitespace-nowrap transition-all ${
        size === "xs" ? "px-2 py-1 text-[11px]" : "px-3.5 py-1.5 text-[13px]"
      } ${className}`}
      style={solid ? {
        backgroundColor: color,
        color: '#fff',
        border: `1px solid ${color}`,
      } : {
        backgroundColor: `${color}${bgAlpha}`,
        color: color,
        border: `1px solid ${color}30`,
      }}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full shadow-sm ${solid ? 'bg-white/70' : ''}`} style={solid ? undefined : { backgroundColor: color }} />}
      {label}
    </span>
  );
}

interface CardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
  style?: React.CSSProperties;
  [key: string]: any; // Allow for other props like key, etc.
}

export function Card({ children, className = "", onClick, hover = true, style, ...props }: CardProps) {
  return (
    <div 
      onClick={onClick}
      style={style}
      className={`
        bg-app-surface border border-app-border rounded-2xl transition-all duration-200
        ${onClick || hover ? "hover:bg-app-surface-up hover:border-app-border-mid" : ""}
        ${onClick ? "cursor-pointer active:scale-[0.98]" : ""}
        ${className}
      `}
      {...props}
    >
      {children}
    </div>
  );
}

export function SLabel({ children, className = "" }: { children: React.ReactNode, className?: string }) {
  return (
    <div className={`text-app-sub text-[13px] font-black tracking-[0.4em] uppercase mb-2.5 px-1 opacity-70 ${className}`}>
      {children}
    </div>
  );
}

/**
 * The small "nothing here yet" note that sits *inside* a populated card —
 * Home's Next Moves / Plans / checklist, Expenses' four settle lists. Distinct
 * from EmptyState, which replaces a whole tab.
 *
 * Nine hand-rolled copies of this existed, identical apart from stray
 * max-widths and padding; the body copy was italic, which the owner moved the
 * larger empty states away from, so it's plain here too.
 *
 * Pass `onClick` to render it as a button (it then carries its own hover);
 * for a note inside an already-clickable card, pass the hover through
 * `className` instead so the card keeps ownership of the interaction.
 */
export function InlineEmpty({ icon, title, body, onClick, className = "" }: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const shared = `w-full flex flex-col items-center justify-center gap-1.5 py-3.5 px-4 text-center opacity-70 ${onClick ? "hover:opacity-90 transition-opacity" : ""} ${className}`;
  const content = (
    <>
      {icon}
      <div className="text-sm font-semibold text-app-sub">{title}</div>
      <div className="text-sm font-medium text-app-sub text-balance max-w-[240px]">{body}</div>
    </>
  );
  return onClick
    ? <button onClick={onClick} className={shared}>{content}</button>
    : <div className={shared}>{content}</div>;
}

// Feature-card background tint — a curated, fixed set of hues (violet,
// emerald, terracotta) desaturated (~38%) from the app's own DEST_GRADIENTS.
// Each streak keeps ONE fixed colour throughout: an earlier pass cycled every
// blob through the whole palette, which read as "changing colour" rather than
// as distinct coloured streaks. They're still free to overlap and blend in
// transit, just never change hue themselves. Shared by Home's feature card and
// the empty states so the two never drift apart.
export const TINT_STREAK_COLORS = ['#452873', '#184539', '#a3512e'];

/**
 * The three drifting, blurred colour streaks behind Home's feature card,
 * reused by the empty states, the notification screen, and the planning
 * tabs' own page background. Elongated ellipses (not circles) that stretch at
 * the fast midpoint of their sweep, which is what sells the "trailing" look —
 * a plain circle sliding sideways just reads as a blob in motion. One streak
 * per horizontal third so their orbits mostly stay out of each other's way.
 * Keyframes (card-tint-wave-a/b/c) live in index.css.
 *
 * The parent must clip this: `overflow-hidden` plus `contain: paint`, since
 * plain overflow-hidden can fail to clip a blurred, animated descendant on
 * WebKit once it's promoted to its own compositing layer.
 */
export function CardTintStreaks({ compact = false }: { compact?: boolean } = {}) {
  // Archive uses smaller blobs; colours, opacity and motion stay shared.
  const box = compact ? ['w-28 h-16', 'w-28 h-14', 'w-28 h-16'] : ['w-40 h-24', 'w-40 h-20', 'w-40 h-24'];
  const dim = [0.38, 0.34, 0.32];
  return (
    <div className="card-tint-streaks absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <div
        className={`absolute top-6 left-[17%] ${box[0]} -translate-x-1/2 rounded-full blur-xl`}
        style={{ backgroundColor: TINT_STREAK_COLORS[0], opacity: dim[0], animation: 'card-tint-wave-a 24s ease-in-out infinite', '--wave-scale': 0.4 } as React.CSSProperties}
      />
      <div
        className={`absolute bottom-6 left-1/2 ${box[1]} -translate-x-1/2 rounded-full blur-xl`}
        style={{ backgroundColor: TINT_STREAK_COLORS[1], opacity: dim[1], animation: 'card-tint-wave-b 30s ease-in-out infinite', animationDelay: '-9s', '--wave-scale': 0.4 } as React.CSSProperties}
      />
      <div
        className={`absolute top-1/2 left-[83%] ${box[2]} -translate-x-1/2 -translate-y-1/2 rounded-full blur-xl`}
        style={{ backgroundColor: TINT_STREAK_COLORS[2], opacity: dim[2], animation: 'card-tint-wave-c 27s ease-in-out infinite', animationDelay: '-16s' }}
      />
    </div>
  );
}

/** The faint paper grain Home's feature card carries, so reuses match it. */
export function PaperTexture() {
  return (
    <div
      className="absolute inset-0 opacity-[0.02] pointer-events-none"
      style={{ backgroundImage: `url("https://www.transparenttextures.com/patterns/natural-paper.png")` }}
    />
  );
}

/**
 * The "nothing here yet" card every tab falls back to — one definition so all
 * five tabs read as the same app rather than five variations on a theme.
 *
 * Built from Home's feature card: same paper grain, same drifting colour
 * streaks, same muted grey caption — so the app a new account meets before
 * creating a trip looks like the app they get afterwards, rather than the
 * poster-style gold circle and serif-italic prose these screens used to carry.
 *
 * Renders only the card: callers own the surrounding layout. ItineraryTab in
 * particular must keep its own wrapping <div> (see itineraryImportHost.test.ts)
 * so the keyed import modal stays a direct child of the same host element
 * across the empty/active branches.
 */
/**
 * Holds a tab's one-time intro card in front of its real content, then
 * dissolves between the two when it's dismissed.
 *
 * `mode="wait"` is the point: the card fades out completely before the tab
 * fades in, so the transition reads as the intro giving way rather than the
 * two cross-fading through each other. Both branches carry the caller's own
 * className, because the layout a tab needs (a fixed-height flex column, for
 * the ones whose header/scroll split depends on it) has to hold either way.
 */
export function IntroGate({ showIntro, intro, className, children }: {
  showIntro: boolean;
  intro: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      {showIntro ? (
        <motion.div
          key="intro"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className={className ?? 'animate-fade-up flex-1 flex flex-col'}
        >
          {intro}
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function EmptyState({ icon, label, title, body, actionLabel, onAction, secondary }: {
  icon: React.ReactNode;
  /** Small caps eyebrow — normally the tab's own name, so the card is clearly "this tab, empty". */
  label: string;
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Optional lesser action rendered under the primary button. */
  secondary?: React.ReactNode;
}) {
  return (
    <Card
      hover={false}
      style={{ contain: 'paint' }}
      // surface-solid: this card carries its own streaks, so it must keep an
      // opaque fill even on a tinted tab (the Schedule intro card is exactly
      // that case). Two sets of drifting colour through each other is murk.
      // Fills the height its caller gives it (every no-trip branch returns
      // before its tab renders any chrome, so that's the viewport on all five).
      // The min-h is a floor for very short viewports, not the usual case —
      // without it the card could shrink below its own content.
      className="surface-solid bg-white flex-1 min-h-[420px] flex flex-col overflow-hidden border border-app-border shadow-none relative"
    >
      <PaperTexture />
      <CardTintStreaks />
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center text-balance px-8 py-10">
        <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center text-gold mb-8">
          {icon}
        </div>
        <SLabel>{label}</SLabel>
        <h2 className="font-serif text-3xl font-bold text-app-text tracking-tight mb-2.5">{title}</h2>
        {/* Sans, muted grey — the same caption treatment as the feature card's
            travelling-mode subtext, not the design system's full-strength
            .ds-body-sm, which reads too heavy against the tinted background. */}
        <p className="text-app-sub/60 text-sm font-sans leading-relaxed max-w-[260px] mx-auto">{body}</p>
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className="w-full max-w-[260px] mt-9 py-4 bg-gold text-white rounded-xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-95 transition"
          >
            {actionLabel}
          </button>
        )}
        {secondary && <div className="mt-4">{secondary}</div>}
      </div>
    </Card>
  );
}

/**
 * The shared category selector: one gold Lucide icon per category in a single
 * inset row, with the selected one lifted onto a white tile.
 *
 * It lives here because every place that asks "what kind of thing is this?"
 * should look and behave the same — expenses, the schedule's add-spot form,
 * and anything added later. `flash` pulses a tile, which is how an automatic
 * suggestion announces itself: the traveller sees the category move under
 * their typing rather than finding it silently changed.
 */
export function CategoryPicker<T extends string>({ options, value, onChange, flash, className = '' }: {
  options: { key: T; label: string; icon: LucideIcon }[];
  value: T | null;
  onChange: (key: T) => void;
  /** Key to pulse once — set it, then clear it after the animation. */
  flash?: T | null;
  className?: string;
}) {
  return (
    <div className={`flex gap-1.5 bg-app-faint/30 p-1 rounded-xl border border-app-border/40 overflow-x-auto no-scrollbar ${className}`}>
      {options.map(({ key, label, icon: Icon }) => {
        const selected = value === key;
        const flashing = flash === key;
        return (
          <motion.button
            key={key}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={selected}
            onClick={() => onChange(key)}
            animate={flashing ? { scale: [1, 1.22, 1] } : { scale: selected ? 1.02 : 1 }}
            transition={flashing ? { duration: 0.5, ease: 'easeOut' } : { duration: 0.2 }}
            className={`flex-1 min-w-[48px] flex items-center justify-center py-2.5 rounded-lg shrink-0 ${
              selected
                ? `bg-white shadow-sm text-gold ${flashing ? 'ring-2 ring-gold/50' : 'ring-1 ring-app-border/10'}`
                : 'text-app-sub hover:text-gold transition-colors'
            }`}
          >
            <Icon className="w-4 h-4" />
          </motion.button>
        );
      })}
    </div>
  );
}

// ── Form fields ───────────────────────────────────────────────────────────────

/**
 * The app has two intended text-field shapes; it had accumulated 48 distinct
 * class strings expressing them (see tasks/design-baseline.md, "D02 forms
 * audit"). These are the two, so the next field added copies a name rather
 * than a string.
 *
 * Neither carries a border COLOUR — that is the error state's job, and a base
 * that set its own would collide with the one the caller appends. Tailwind
 * resolves conflicting utilities by CSS source order, not by their order in
 * the string, so "${FIELD} border-rose-500" is not reliably red. Compose with
 * `fieldBorder` instead and there is nothing to collide.
 */

/**
 * The two ways an error is shown. A line under the thing it's about, or a
 * soft box when it stands on its own above a form. Sentence case, never the
 * uppercase label style — an error is read, not scanned.
 */
export const ERROR_LINE = 'text-sm text-rose-600';
export const ERROR_BOX = 'rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700';

/** Compact field — the import review tables and inline editors. */
export const FIELD_COMPACT =
  'w-full font-sans px-3 py-2 bg-app-faint rounded-xl text-sm border focus:outline-none focus:border-gold/40 transition-colors';

/** Full form field — the form-heavy screens. */
export const FIELD =
  'w-full font-sans bg-white border rounded-xl px-4 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 transition-all placeholder:text-app-sub/40 appearance-none';

/**
 * Border + ring for a field, by state. `strong` is the full field's heavier
 * treatment; the compact field uses the lighter one.
 *
 * The gold focus ring lives in the non-error branch on purpose: a field marked
 * red should stay red while it has focus, not turn gold the moment the user
 * goes to fix it.
 */
export const fieldBorder = (error?: boolean, strong = false) =>
  error
    ? (strong ? 'border-rose-500 ring-1 ring-rose-500' : 'border-rose-400 ring-1 ring-rose-100')
    : (strong ? 'border-app-border/60 focus:ring-gold/5 focus:border-gold/40' : 'border-app-border/40');

/**
 * Sign-in / account field — taller, softer-cornered, lighter weight than
 * FIELD. A third shape rather than a variant of the other two because these
 * screens are read cold by someone who has never used the app.
 *
 * Carries no horizontal padding: these fields sit behind leading icons at
 * several different insets, so the caller supplies `px-5`, `pl-11 pr-4` and so
 * on. Pair with `fieldBorder(error, true)` as the others do.
 */
export const FIELD_AUTH =
  'w-full font-sans bg-white border rounded-2xl py-4 text-sm font-semibold text-app-text outline-none shadow-sm transition-all';

export const AUTH_ERROR =
  'rounded-xl bg-rose-50/80 px-4 py-3 text-left font-sans text-base font-normal leading-relaxed text-rose-700';

// ── Booking status ──────────────────────────────────────────────────────────

/**
 * A thin confirmed-booking strip along a card's bottom edge — a Shortlist
 * attraction/eat that came from a parsed booking (a dinner reservation, a
 * ticketed tour) carries `booked`/`ref` today with nothing showing it. The
 * card must be `relative overflow-hidden` for this to sit flush against the
 * rounded corners rather than poking past them.
 *
 * Deliberately not the same colour as the card's own "in schedule" toggle
 * (emerald, on the attraction/eat cards) — that is a different fact
 * (has this been placed on a day?) from this one (is there a real booking
 * behind it?), and reusing the colour would blur two states that can each be
 * true or false independently of the other.
 */
/** The bottom-edge strip on a booked Shortlist/Itinerary card — date + time
 *  only. Confirmation ref and other booking details live in the item's
 *  detail view (opened on click), not here. */
export function BookedStrip({ label = 'Booked', date, time }: { label?: string; date?: string; time?: string }) {
  return (
    <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 border-t border-sky-100">
      <CheckCircle2 className="w-3 h-3 text-sky-600 shrink-0" />
      <span className="text-[11px] font-semibold text-sky-700 truncate">
        {label}{date ? ` · ${date}` : ''}{time ? ` · ${formatClock(time)}` : ''}
      </span>
    </div>
  );
}
